/**
 * Utility functions for parsing and managing JWT claims from the claims cookie
 * and session timer from the SessionTimer cookie
 */

/**
 * Parse JWT token and extract claims
 * @param {string} token - JWT token string
 * @returns {Object|null} - Parsed claims or null if invalid
 */
export const parseJWTClaims = (token) => {
    try {
        if (!token) {
            return null;
        }

        // JWT has 3 parts separated by dots: header.payload.signature
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        // Decode the payload (middle part)
        const payload = parts[1];
        const decodedPayload = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));

        const claims = JSON.parse(decodedPayload);
        return claims;
    } catch (error) {
        console.error('Error parsing JWT claims:', error);
        return null;
    }
};

/**
 * Get user ID cookie value from document.cookie
 * @returns {string|null} - UserID cookie value or null if not found
 */
export const getUserIDCookie = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'UserID' || name === 'userid') {
            return value;
        }
    }
    return null;
};

/**
 * Get session timer cookie value from document.cookie
 * @returns {string|null} - SessionTimer cookie value or null if not found
 */
export const getSessionTimerCookie = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'SessionTimer') {
            return value;
        }
    }
    return null;
};

/**
 * Get session deadline cookie value from document.cookie
 * @returns {string|null} - SessionDeadline cookie value or null if not found
 */
export const getSessionDeadlineCookie = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'SessionDeadline') {
            return value;
        }
    }
    return null;
};

/**
 * Get expiration time from SessionDeadline cookie (Unix timestamp in seconds)
 * @returns {number|null} - Expiration timestamp in milliseconds or null if not found
 */
export const getSessionDeadlineExpiration = () => {
    const sessionDeadline = getSessionDeadlineCookie();

    if (!sessionDeadline) {
        return null;
    }

    try {
        const timestampSeconds = parseInt(sessionDeadline);
        if (isNaN(timestampSeconds) || timestampSeconds <= 0) {
            return null;
        }

        const expirationMs = timestampSeconds * 1000;
        return expirationMs;
    } catch (error) {
        return null;
    }
};

/**
 * Get expiration time from SessionTimer cookie (Unix timestamp in seconds)
 * @returns {number|null} - Expiration timestamp in milliseconds or null if not found
 */
export const getSessionTimerExpiration = () => {
    const sessionTimer = getSessionTimerCookie();

    if (!sessionTimer) {
        return null;
    }

    try {
        const timestampSeconds = parseInt(sessionTimer);
        if (isNaN(timestampSeconds) || timestampSeconds <= 0) {
            return null;
        }

        const expirationMs = timestampSeconds * 1000;
        return expirationMs;
    } catch (error) {
        return null;
    }
};

/**
 * Get the raw SessionTimer cookie value (for comparison to detect if user is active)
 * @returns {string|null} - SessionTimer cookie value or null if not found
 */
export const getSessionTimerValue = () => {
    return getSessionTimerCookie();
};

/**
 * Check if SessionDeadline cookie is expired
 * @returns {boolean} - True if expired or not found
 */
export const isSessionExpired = () => {
    const expiration = getSessionDeadlineExpiration();
    if (!expiration) {
        return true;
    }
    return Date.now() >= expiration;
};

/**
 * Validate that session cookies exist and are not expired
 * Authentication requires UserID, SessionTimer, and SessionDeadline cookies
 * @returns {boolean} - True if session is valid (all cookies exist and not expired)
 */
export const isSessionValid = () => {
    const userID = getUserIDCookie();
    const sessionDeadline = getSessionDeadlineCookie();

    // Session validity = UserID + SessionDeadline not expired. Do NOT require SessionTimer:
    // the backend sets SessionTimer cookie with Max-Age = timer duration (e.g. 60s), so the
    // browser removes it when the timer expires. The session is still valid until SessionDeadline.
    if (!userID || !sessionDeadline) {
        return false;
    }

    return !isSessionExpired();
};

/**
 * Get time until SessionDeadline expires in milliseconds
 * @returns {number|null} - Milliseconds until expiration or null if not found/expired
 */
export const getTimeUntilExpiration = () => {
    const expiration = getSessionDeadlineExpiration();
    if (!expiration) return null;

    const timeUntil = expiration - Date.now();
    return timeUntil > 0 ? timeUntil : null;
};

/**
 * Parse base64 encoded claims from response header
 * @param {Object} response - Axios response object
 * @param {string} headerName - Name of the header containing base64 encoded claims
 * @returns {Object|null} - Parsed claims or null if not found/invalid
 */
export const parseClaimsFromHeader = (response, headerName = 'x-user-context') => {
    try {
        if (!response || !response.headers) {
            return null;
        }

        // Axios normalizes headers to lowercase
        let encodedClaims = response.headers[headerName.toLowerCase()] ||
                           response.headers['x-user-context'];
        
        // Try iterating through headers if direct access didn't work
        if (!encodedClaims) {
            const lowerHeaderName = headerName.toLowerCase();
            for (const key in response.headers) {
                if (key.toLowerCase() === lowerHeaderName) {
                    encodedClaims = response.headers[key];
                    break;
                }
            }
        }

        if (!encodedClaims) {
            return null;
        }

        // Decode base64 string and parse JSON
        const decodedClaims = atob(encodedClaims);
        return JSON.parse(decodedClaims);
    } catch (error) {
        return null;
    }
};

/**
 * Get user information from claims header (this should be called with a response object)
 * @param {Object} response - Axios response object
 * @returns {Object|null} - User claims or null if not found
 */
export const getUserFromClaims = (response) => {
    if (!response) return null;

    return parseClaimsFromHeader(response);
};
